/**
 * Symbol picker for the Markets page. Searches the real PSX catalogue; with
 * an empty query it lists the hot set (holdings, watchlists, baseline names)
 * with their live prices and day change.
 */
import { useEffect, useRef, useState } from 'react';
import { searchSymbols, useMovers, type SymbolHit } from '../../lib/dataHooks';

interface Props {
  currentSym: string;
  onPick: (sym: string) => void;
}

interface Row {
  sym: string;
  name: string;
  price: number | null;
  change: number | null;
  inactive?: boolean;
}

const toRow = (h: SymbolHit): Row => ({
  sym: h.sym,
  name: h.name,
  price: h.lastPrice !== null && h.lastPrice !== undefined ? Number(h.lastPrice) : null,
  change: h.dayChangePct !== null && h.dayChangePct !== undefined ? Number(h.dayChangePct) : null,
  inactive: h.isActive === false,
});

export const SymbolSelector = ({ currentSym, onPick }: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Row[]>([]);
  const [searching, setSearching] = useState(false);
  const [cursor, setCursor] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const hot = useMovers(30, open ? 15_000 : 0, 'active');

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else {
      setQuery('');
      setHits([]);
    }
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (!open || !term) {
      setHits([]);
      setSearching(false);
      return;
    }
    const mine = ++seq.current;
    setSearching(true);
    const timer = setTimeout(() => {
      searchSymbols(term, 20)
        .then((r) => {
          if (mine !== seq.current) return;
          setHits(r.results.filter((h) => !h.market || h.market === 'REG').map(toRow));
          setCursor(0);
        })
        .catch(() => mine === seq.current && setHits([]))
        .finally(() => mine === seq.current && setSearching(false));
    }, 160);
    return () => clearTimeout(timer);
  }, [query, open]);

  const searchingMode = query.trim().length > 0;
  const rows: Row[] = searchingMode
    ? hits
    : (hot.data?.gainers ?? []).map((m) => ({ sym: m.sym, name: '', price: m.last, change: m.changePct }));

  const pick = (sym: string) => {
    onPick(sym.toUpperCase());
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Change symbol"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: open ? 'var(--tk-surface-2)' : 'transparent', border: '1px solid var(--tk-line)', borderRadius: 'var(--tk-radius-sm)', color: 'var(--tk-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s var(--tk-ease)' }}
      >
        Change
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 400, maxWidth: 'calc(100vw - 32px)', maxHeight: 480, overflow: 'hidden', background: 'var(--tk-surface)', border: '1px solid var(--tk-line)', borderRadius: 'var(--tk-radius-md)', boxShadow: 'var(--tk-shadow-lg)', zIndex: 200, display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--tk-line-2)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--tk-ink-3)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCursor((c) => Math.min(rows.length - 1, c + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCursor((c) => Math.max(0, c - 1));
                } else if (e.key === 'Enter') {
                  const r = rows[cursor];
                  if (r) pick(r.sym);
                } else if (e.key === 'Escape') setOpen(false);
              }}
              placeholder="Search symbol or company"
              spellCheck={false}
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--tk-ink)', fontSize: 13.5, fontWeight: 500, fontFamily: 'var(--tk-sans)' }}
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {rows.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--tk-ink-3)' }}>
                {searchingMode ? (searching ? 'Searching…' : `No matches for "${query.trim()}"`) : 'Type to search the PSX catalogue.'}
              </div>
            )}
            {rows.map((r, i) => {
              const active = r.sym === currentSym;
              return (
                <div
                  key={r.sym}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(r.sym)}
                  style={{ display: 'grid', gridTemplateColumns: '78px 1fr auto auto', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', background: i === cursor ? 'var(--tk-surface-2)' : active ? 'var(--tk-accent-soft)' : 'transparent', transition: 'background 0.12s var(--tk-ease)' }}
                >
                  <span style={{ fontFamily: 'var(--tk-mono)', fontSize: 13, fontWeight: 700, color: active ? 'var(--tk-accent)' : 'var(--tk-ink)' }}>{r.sym}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--tk-ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.name}
                    {r.inactive ? <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--tk-ink-3)' }}>not trading</span> : null}
                  </span>
                  <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--tk-ink-2)', textAlign: 'right' }}>
                    {r.price !== null ? r.price.toLocaleString('en-PK', { maximumFractionDigits: 2 }) : ''}
                  </span>
                  <span className="mono" style={{ minWidth: 58, textAlign: 'right', fontSize: 11.5, fontWeight: 700, color: r.change === null ? 'var(--tk-ink-3)' : r.change >= 0 ? 'var(--tk-up)' : 'var(--tk-down)' }}>
                    {r.change !== null ? `${r.change >= 0 ? '+' : ''}${r.change.toFixed(2)}%` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
